import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Reveal from "@/components/common/Reveal";
import { cn } from "@/lib/utils";

const JOURNEY = [
  { date: "June 2019", title: "Foundation & Vision", desc: "Founded in 2019, driven to elevate customer satisfaction and transform automotive dealership experiences." },
  { date: "April 2020", title: "Innovation in Challenging Times", desc: "Pledged to promote recycling and reuse, embracing compostable products and eliminating plastic usage." },
  { date: "August 2021", title: "Capability Expansion", desc: "Scaled our private-labeling business by partnering with various automakers." },
  { date: "September 2022", title: "OEM Rollout at Scale", desc: "Delivered bespoke solutions to dealerships and OEMs." },
  { date: "April 2023", title: "OEM Portfolio Expansion", desc: "Expanded our portfolio with OEM-grade polishes and aerosols under private labelling." },
  { date: "April 2024", title: "Portfolio Expansion, 2\u00D7 Growth", desc: "Maintained 2x growth through strategic expansion of our product portfolio." },
  { date: "July 2025", title: "Innovative Packaging Formats", desc: "Introduced new packaging formats including sachets, cans and bottles." },
  { date: "February 2026", title: "Brand Launch & Retail Expansion", desc: "Launched AutoCareX, expanding into omnichannel retail." },
];

function JourneyCard({ m, mobile = false }) {
  return (
    <div
      className={cn(
        "w-full px-2 transition-transform duration-200 group-hover:-translate-y-0.5 lg:px-3",
        mobile ? "text-left" : "text-center"
      )}
    >
      <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#E11B22]">{m.date}</p>
      <p className="mt-1.5 font-display text-[15px] font-bold leading-snug text-white">{m.title}</p>
      <p className="mt-2 text-[12.5px] leading-relaxed text-white/55">{m.desc}</p>
    </div>
  );
}

function JourneyNode() {
  return (
    <span className="relative z-10 block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#E11B22] bg-[var(--acx-bg)] transition-all duration-200 group-hover:scale-125 group-hover:shadow-[0_0_0_6px_rgba(225,27,34,0.18)]" />
  );
}

// Shared "Our Journey" timeline section — rendered on the homepage AND the
// /about/our-journey page so content + styling stay identical everywhere.
export function OurJourney({ compact = false }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 78%", "end 62%"] });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={ref}
      className={cn(
        "relative acx-noise bg-[var(--acx-bg)]",
        !compact && "border-t border-[color:var(--acx-border)]"
      )}
      data-testid="home-journey"
    >
      <div className={cn(
        "acx-container px-4 sm:px-6 lg:px-10",
        compact ? "pt-4 pb-20 lg:pt-6 lg:pb-24" : "py-20 lg:py-28"
      )}>
        <Reveal>
          <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Milestones</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
            Our Journey
          </h2>
          <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-white/60">
            From a bold idea in 2019 to the launch of AutoCareX — the milestones that shaped us.
          </p>
        </Reveal>

        {/* DESKTOP: horizontal, stepped (alternating above/below) timeline */}
        <div className="relative mt-16 hidden lg:block" data-testid="journey-desktop">
          <div className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 bg-white/10" />
          <motion.div
            className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 origin-left bg-[#E11B22]"
            style={reduce ? { scaleX: 1 } : { scaleX }}
          />
          <div className="relative grid grid-cols-8">
            {JOURNEY.map((m, i) => {
              const above = i % 2 === 0;
              return (
                <motion.div
                  key={m.date}
                  initial={reduce ? false : { opacity: 0, y: above ? -22 : 22 }}
                  whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative flex flex-col items-center"
                >
                  <div className="flex h-[230px] w-full flex-col items-center justify-end">
                    {above && (
                      <>
                        <JourneyCard m={m} />
                        <span className="mt-3 h-5 w-px bg-white/15 transition-colors duration-200 group-hover:bg-[#E11B22]/50" />
                      </>
                    )}
                  </div>
                  <JourneyNode />
                  <div className="flex h-[230px] w-full flex-col items-center justify-start">
                    {!above && (
                      <>
                        <span className="mb-3 h-5 w-px bg-white/15 transition-colors duration-200 group-hover:bg-[#E11B22]/50" />
                        <JourneyCard m={m} />
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* MOBILE / TABLET: vertical stacked timeline */}
        <div className="relative mt-12 lg:hidden" data-testid="journey-mobile">
          <div className="absolute bottom-0 left-[14px] top-0 w-[2px] bg-white/10" />
          <motion.div
            className="absolute left-[14px] top-0 h-full w-[2px] origin-top bg-[#E11B22]"
            style={reduce ? { scaleY: 1 } : { scaleY }}
          />
          <div className="space-y-9">
            {JOURNEY.map((m, i) => (
              <motion.div
                key={m.date}
                initial={reduce ? false : { opacity: 0, x: 20 }}
                whileInView={reduce ? undefined : { opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="group relative pl-12"
              >
                <span className="absolute left-[8px] top-1 z-10">
                  <JourneyNode />
                </span>
                <JourneyCard m={m} mobile />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default OurJourney;
